import React from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { colors, fontSize } from "style";
import { createMarkup } from "utils";
import { CHALLENGE_ITEM_TYPES } from "constants/index";
import Icon, { ICON_SIZE, ICON_TYPE } from "components/Icon";

/**
 * ChallengeItemSidebar
 *
 * @param {Object}    stats
 * @param {Object}    categorizedTasks
 * @param {Object}    collapsedGroups
 * @param {Number}    activeItemIndex
 * @param {Function}  setActiveItemIndex
 * @param {Function}  toggleGroup
 * @param {Number}    totalItemsCount
 */
const ChallengeItemSidebar = ({
  stats,
  categorizedTasks,
  collapsedGroups,
  activeItemIndex,
  setActiveItemIndex,
  toggleGroup,
  totalItemsCount,
}) => (
  <div css={styles.sidebar}>
    <div css={styles.overviewHeader}>
      <div css={styles.statItem}>
        <span css={styles.statValue}>{totalItemsCount}</span>
        <span css={styles.statLabel}>
          <FormattedMessage id="CodeChallenges.ChallengeItemSidebar.TotalItems" />
        </span>
      </div>
      {Object.entries(GROUPS).map(([type, config]) => (
        <div key={type} css={styles.statItem}>
          <span css={styles.statValue}>{stats[config.key]}</span>
          <span css={styles.statLabel}>{config.key.toUpperCase()}</span>
        </div>
      ))}
    </div>

    <div css={styles.sidebarListScroll}>
      {Object.entries(GROUPS).map(([type, config]) => {
        const { key, title, icon, color, activeStyle, badgeStyle, headerStyle } = config;
        const tasks = categorizedTasks[key] || [];

        if (!tasks || tasks.length === 0) return null;
        const isCollapsed = collapsedGroups[key];

        return (
          <div key={type} css={styles.taskGroup}>
            <div css={[styles.groupHeader, headerStyle]} onClick={() => toggleGroup(key)}>
              <div css={styles.groupHeaderTitle}>
                <Icon type={icon} size={ICON_SIZE.small} color={color} />
                <span>
                  {title} ({tasks.length})
                </span>
              </div>
              <div css={[styles.chevron, isCollapsed && styles.chevronRotated]}>
                <Icon type={ICON_TYPE.chevronDown} size={ICON_SIZE.small} color={color} />
              </div>
            </div>

            {!isCollapsed && (
              <div css={styles.groupBody}>
                {tasks.map((task, idx) => {
                  const isActive = activeItemIndex === task.originalIndex;
                  return (
                    <div
                      key={task.id}
                      css={[styles.taskItemCard, isActive && activeStyle]}
                      onClick={() => setActiveItemIndex(task.originalIndex)}
                    >
                      <span css={[styles.itemNumberBadge, isActive && badgeStyle]}>{idx + 1}</span>
                      <div css={styles.itemCardTextWrapper}>
                        <h4 css={styles.itemCardTitle} dangerouslySetInnerHTML={createMarkup(task.title)} />
                        {task.metadata && (
                          <span css={styles.itemCardMeta}>
                            {task.type === CHALLENGE_ITEM_TYPES.CODING
                              ? `${task.metadata.language} • ${task.metadata.tags?.[0] || ""} • ${
                                  task.metadata.difficulty || ""
                                }`
                              : task.metadata.tags?.join(", ")}
                          </span>
                        )}
                      </div>
                      <Icon
                        type={ICON_TYPE.chevronRight}
                        size={ICON_SIZE.small}
                        color={isActive ? color : colors.grayAnatomyLight1}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const styles = {
  sidebar: css`
    width: 350px;
    min-width: 350px;
    border-right: 1px solid ${colors.grayAnatomyLight3};
    display: flex;
    flex-direction: column;
    background-color: ${colors.grayAnatomyLight5};
  `,
  overviewHeader: css`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    padding: 12px;
    background-color: ${colors.white};
    border-bottom: 1px solid ${colors.grayAnatomyLight3};
  `,
  statItem: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    border-right: 1px solid ${colors.grayAnatomyLight4};
    &:last-of-type {
      border-right: none;
    }
  `,
  statValue: css`
    font-size: ${fontSize.normal};
    font-weight: 700;
    color: ${colors.grayAnatomyDark};
  `,
  statLabel: css`
    font-size: ${fontSize.xxxsmall};
    color: ${colors.grayAnatomyBase};
    margin-top: 2px;
    text-align: center;
  `,
  sidebarListScroll: css`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  taskGroup: css`
    display: flex;
    flex-direction: column;
  `,
  groupHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    font-size: ${fontSize.xxsmall};
    font-weight: 700;
    cursor: pointer;
    border-radius: 6px;
    user-select: none;
  `,
  codingGroupColor: css`
    background-color: ${colors.purpleRainLight8};
    color: ${colors.purpleRainDark2};
  `,
  mcqGroupColor: css`
    background-color: ${colors.green6};
    color: ${colors.darkGreen};
  `,
  videoGroupColor: css`
    background-color: ${colors.pinkPantherLight1};
    color: ${colors.pinkPantherBase};
  `,
  groupHeaderTitle: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  chevron: css`
    display: flex;
    transition: transform 0.2s ease;
  `,
  chevronRotated: css`
    transform: rotate(-90deg);
  `,
  groupBody: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    padding-left: 4px;
  `,
  taskItemCard: css`
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border: 1px solid ${colors.grayAnatomyLight3};
    border-radius: 8px;
    background-color: ${colors.white};
    cursor: pointer;
    gap: 12px;
    transition: all 0.2s;
    &:hover {
      border-color: ${colors.grayAnatomyLight2};
    }
  `,
  taskItemCardActiveCoding: css`
    border-color: ${colors.purpleRainBase};
    box-shadow: 0 0 0 1px ${colors.purpleRainBase};
  `,
  taskItemCardActiveMcq: css`
    border-color: ${colors.green};
    box-shadow: 0 0 0 1px ${colors.green};
  `,
  taskItemCardActiveVideo: css`
    border-color: ${colors.pinkPantherBase};
    box-shadow: 0 0 0 1px ${colors.pinkPantherBase};
  `,
  itemNumberBadge: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    background-color: ${colors.grayAnatomyLight4};
    border-radius: 4px;
    font-size: ${fontSize.xxsmall};
    font-weight: 700;
    color: ${colors.grayAnatomyBase};
  `,
  itemNumberActiveCoding: css`
    background-color: ${colors.purpleRainLight3};
    color: ${colors.purpleRainDark1};
  `,
  itemNumberActiveMcq: css`
    background-color: ${colors.green6};
    color: ${colors.darkGreen};
  `,
  itemNumberActiveVideo: css`
    background-color: ${colors.pinkPantherLight1};
    color: ${colors.pinkPantherBase};
  `,
  itemCardTextWrapper: css`
    flex: 1;
    min-width: 0;
  `,
  itemCardTitle: css`
    font-size: ${fontSize.xsmall};
    font-weight: 600;
    color: ${colors.grayAnatomyDark};
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    p {
      margin: 0;
    }
  `,
  itemCardMeta: css`
    font-size: ${fontSize.xxsmall};
    color: ${colors.grayAnatomyLight1};
  `,
};

const GROUPS = {
  [CHALLENGE_ITEM_TYPES.CODING]: {
    key: "coding",
    title: "CODING TASKS",
    icon: ICON_TYPE.code,
    color: colors.purpleRainBase,
    activeStyle: styles.taskItemCardActiveCoding,
    badgeStyle: styles.itemNumberActiveCoding,
    headerStyle: styles.codingGroupColor,
  },
  [CHALLENGE_ITEM_TYPES.MCQ]: {
    key: "mcq",
    title: "MULTIPLE CHOICE",
    icon: ICON_TYPE.listLines,
    color: colors.green,
    activeStyle: styles.taskItemCardActiveMcq,
    badgeStyle: styles.itemNumberActiveMcq,
    headerStyle: styles.mcqGroupColor,
  },
  [CHALLENGE_ITEM_TYPES.OPEN_ENDED]: {
    key: "video",
    title: "OPEN ENDED",
    icon: ICON_TYPE.video,
    color: colors.pinkPantherBase,
    activeStyle: styles.taskItemCardActiveVideo,
    badgeStyle: styles.itemNumberActiveVideo,
    headerStyle: styles.videoGroupColor,
  },
};

ChallengeItemSidebar.propTypes = {
  stats: PropTypes.object.isRequired,
  categorizedTasks: PropTypes.object.isRequired,
  collapsedGroups: PropTypes.object.isRequired,
  activeItemIndex: PropTypes.number.isRequired,
  setActiveItemIndex: PropTypes.func.isRequired,
  toggleGroup: PropTypes.func.isRequired,
  totalItemsCount: PropTypes.number.isRequired,
};

export default ChallengeItemSidebar;
